/**
 * Os documentos por CCB no Conferir (E177.F5, GUS-253, ADR-0130; HTML 6.8 por CCB
 * `?tela=conferencia-ccb`): um grupo recolhido por CCB, "CCB 4201 · 2 documentos", com cada
 * documento ("Somente esta CCB", "Compartilhado com", "Compartilhado · enviado na") e "Editar
 * documentos e vínculos".
 *
 * E177.F4.C02 (F-E177.F4-02): cada documento abre antes da conclusão, e o homônimo de outro
 * documento leva o identificador curto.
 */
import { useState } from 'react';
import type { AxiosInstance } from 'axios';

import { Badge, Button } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import type { ContratoDoEnvioData } from '@/lib/abertura_do_caso';

import { AbrirArquivo } from './AbrirArquivo';
import { blocosDoEnvio, type BlocoDaCcb } from './blocosDasCcbs';
import { contagensDoBloco, documentosDoGrupo, tituloDoGrupo } from './conferenciaPorCcb';
import { Recolhivel } from './PecasDaAbertura';

const t = ptBR.abertura.conferirPorCcb;

function GrupoDaCcb({
  client,
  caseId,
  bloco,
  blocos,
  onEditar,
}: {
  client: AxiosInstance;
  caseId: string;
  bloco: BlocoDaCcb;
  blocos: readonly BlocoDaCcb[];
  onEditar: () => void;
}): JSX.Element {
  const [aberto, setAberto] = useState(false);
  return (
    <Recolhivel
      titulo={tituloDoGrupo(bloco, contagensDoBloco(blocos, bloco))}
      aberto={aberto}
      onAberto={setAberto}
      testId={`conferir-grupo-${bloco.chave}`}
    >
      <ul>
        {documentosDoGrupo(blocos, bloco).map((d) => (
          <li
            key={d.documentoId}
            className="flex items-center justify-between gap-[15px] border-b border-border py-3 text-[14px] last:border-b-0"
            data-testid={`conferir-grupo-${bloco.chave}-${d.documentoId}`}
          >
            <span className="min-w-0">
              <b className="block truncate font-semibold text-fg">{d.nome}</b>
              <small className="block text-[13px] text-fg-muted">{d.detalhe}</small>
            </span>
            <span className="flex flex-wrap items-center gap-3">
              <AbrirArquivo
                client={client}
                caseId={caseId}
                documentoId={d.documentoId}
                nome={d.nome}
              />
              <Badge variant="neutral">{t.recebido}</Badge>
            </span>
          </li>
        ))}
      </ul>
      <div>
        <Button type="button" variant="secondary" onClick={onEditar}>
          {t.editar}
        </Button>
      </div>
    </Recolhivel>
  );
}

/** Os documentos agrupados por CCB, cada grupo recolhido. */
export function DocumentosPorCcb({
  client,
  caseId,
  contratos,
  onEditar,
}: {
  client: AxiosInstance;
  caseId: string;
  contratos: readonly ContratoDoEnvioData[];
  onEditar: () => void;
}): JSX.Element | null {
  const blocos = blocosDoEnvio(
    { contratos: [...contratos], perguntas: [], em_verificacao: false },
    [],
  );
  if (blocos.length === 0) return null;
  return (
    <div className="mt-[14px] flex flex-col gap-3" data-testid="conferir-grupos">
      {blocos.map((b) => (
        <GrupoDaCcb
          key={b.chave}
          client={client}
          caseId={caseId}
          bloco={b}
          blocos={blocos}
          onEditar={onEditar}
        />
      ))}
    </div>
  );
}
